import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import hidePopupOperation from 'store/app/operation/hidePopupOperation';
import { PopupPremium, Button } from 'components/Common';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import { nextStepFlowAction } from 'store/app/slice';
import { AvailableFlows, AvailablePopups } from 'store/app/types';
import isDesktop from 'store/app/selector/isDesktop';
import { AppBundle } from 'api/optimalprint-sdk.d';
import FrameSelector from './StudioFrameSelector/StudioFrameSelector';
import styles from './StudioFrameUpsellPopup.module.scss';
import ProductPreview from './StudioProductPreview/StudioProductPreview';
import VoucherText from './StudioVoucherText/StudioVoucherText';
import FramePrice from './StudioFramePrice/StudioFramePrice';

export interface StudioFrameSelectorPopupProps {
  frameProductId: number | undefined;
  popupTitle: string;
  addToBasketCallback: () => void;
  selectedFrame: AppBundle.Api.Response.ProductUpsell.V1.ProductUpsellScreenV1Response.IUpsellItem | undefined;
  categoryId: number | undefined;
  voucher: string;
  previewUrl: string;
  frames: AppBundle.Api.Response.ProductUpsell.V1.ProductUpsellScreenV1Response.IUpsellItem[];
  activeFrameProductId: number | undefined;
  frameTitle: string | undefined;
  frameDescription: string | null | undefined;
  selectFrameCallback: (
    frame: AppBundle.Api.Response.ProductUpsell.V1.ProductUpsellScreenV1Response.IUpsellItem,
    frameIndex: number,
  ) => void;
}

const StudioFrameSelectorPopup: FC<StudioFrameSelectorPopupProps> = ({
  frameProductId,
  popupTitle,
  addToBasketCallback,
  selectedFrame,
  categoryId,
  voucher,
  previewUrl,
  frames,
  activeFrameProductId,
  frameTitle,
  frameDescription,
  selectFrameCallback,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isDesktopDevice = useSelector(isDesktop);

  const closePopup = () => {
    dispatch(hidePopupOperation());
    dispatch(nextStepFlowAction(AvailableFlows.postAddToCart));
  };

  const continueWithoutFrame = () => {
    gtmSendEvent({
      event: 'api_upsell_frames_continue_without_frame',
    });
    closePopup();
  };

  const continueWithFrame = async () => {
    // await dispatch(addToBasketSelectedBundleProductsOperation());
    await addToBasketCallback();
    gtmSendEvent({
      event: 'api_upsell_frames_add_frame_to_basket',
    });
    closePopup();
  };

  const priceAndButtons = () => (
    <>
      <FramePrice selectedFrame={selectedFrame} categoryId={categoryId} />
      <div className={styles.buttonGroup}>
        <Button
          id="continue-without-frame-button"
          onClick={continueWithoutFrame}
          text={t('txt_continue_without_the_frame')}
          className={styles.withoutFrame}
        />
        <Button
          id="add-frame-to-basket-button"
          onClick={continueWithFrame}
          primary
          className={styles.withFrame}
          disabled={frameProductId === 0}
          text={t('txt_button_add_frame_to_the_basket')}
        />
      </div>
    </>
  );

  return (
    <PopupPremium popupId={AvailablePopups.frameUpsellPopup} title={popupTitle} onClosePopup={closePopup}>
      <div className={styles.frameSelectorPopup}>
        <VoucherText voucher={voucher} />
        <div className={styles.contentRow}>
          <div className={styles.frameSelectorColumn}>
            <FrameSelector
              activeFrameProductId={activeFrameProductId}
              frameDescription={frameDescription}
              frameTitle={frameTitle}
              frames={frames}
              selectFrameCallback={selectFrameCallback}
            />
          </div>
          <div className={styles.productPreviewColumn}>
            <ProductPreview previewUrl={previewUrl} />
            {isDesktopDevice && priceAndButtons()}
          </div>
        </div>
        {!isDesktopDevice && priceAndButtons()}
      </div>
    </PopupPremium>
  );
};

export default StudioFrameSelectorPopup;
